import React from "react";
import Card from "components/card";
import MetricCard from "components/card/MetricCard";
import {
    ChartColumn,
    CircleCheck,
    Clock,
    Timer,
    TrendingUp,
    TrendingDown,
    Zap,
} from "lucide-react";

const ProductivityProgressCard = ({ employee }) => {
    if (!employee) return null;

    const { initials, name, role, details } = employee;
    const { department, status, metrics, productivity_score } = details;

    const normalizedStatus = status
        .toLowerCase()
        .replace(/[^a-z\s-]/g, "")
        .replace(/\s+/g, "")
    ;
    const Icon = normalizedStatus === "increase" ? TrendingUp : TrendingDown;

    return (
        <Card className="flex flex-col gap-6 rounded-2xl border-2 border-cyan-500 bg-white shadow-md">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border-b border-slate-200 rounded-t-xl items-start gap-1.5 px-6 pt-6 pb-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <span className="relative flex shrink-0 overflow-hidden rounded-full w-16 h-16 border-2 border-white shadow-md">
                            <span className="flex size-full items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 text-white font-semibold text-[18px]">
                                {initials}
                            </span>
                        </span>
                        <div>
                            <h4 className="text-[24px]">
                                {name}
                            </h4>
                            <p className="text-[13px] text-slate-500">
                                {role}
                            </p>
                            <span className="inline-flex items-center justify-center rounded-xl px-2 py-0.5 text-xs font-medium w-fit shrink-0 mt-1 bg-cyan-100 text-cyan-700 hover:bg-cyan-200">
                                {department}
                            </span>
                        </div>
                    </div>
                    <span className={`flex items-center [&>svg]:size-3 gap-1 status-indicator ${normalizedStatus} text-xs font-medium`}>
                        <Icon className="w-4 h-4" />
                        <span>{status}</span>
                    </span>
                </div>
            </div>
            <div className="pb-6 p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard
                        label="Weekly Avg"
                        Icon={Clock}
                        value={`${metrics.weekly_average}h`}
                        color="blue"
                    />
                    <MetricCard
                        label="Monthly Total"
                        Icon={Timer}
                        value={`${metrics.monthly_total}h`}
                        color="purple"
                    />
                    <MetricCard
                        label="Efficiency"
                        Icon={Zap}
                        value={`${metrics.efficiency}%`}
                        color="green"
                    />
                    <MetricCard
                        label="Tasks Done"
                        Icon={CircleCheck}
                        value={metrics.tasks_done}
                        color="orange"
                    />
                </div>
                <div className="bg-gray-200 h-px w-full my-6"></div>
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-[14px] flex items-center gap-2">
                            <ChartColumn className="w-4 h-4 text-cyan-600" />
                            Productivity Score
                        </h4>
                        <span className="text-[18px] font-semibold">
                            {productivity_score}%
                        </span>
                    </div>
                    <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                        <div
                            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                            style={{ width: `${productivity_score}%` }}
                        />
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default ProductivityProgressCard;